import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const email = "contact@windmill-anime.demo";

const scopeItems = [
  {
    icon: "ri-film-line",
    title: "动画推荐",
    text: "涵盖科幻、奇幻、热血、治愈等多种题材的精选动画，提供剧情简介、评分、配音与作品信息。",
  },
  {
    icon: "ri-newspaper-line",
    title: "动漫新闻",
    text: "聚合新番情报、业界动态、周边活动、演出与深度访谈，帮助用户第一时间了解动漫圈动态。",
  },
  {
    icon: "ri-user-star-line",
    title: "角色介绍",
    text: "整理角色背景、作品关系与代表台词，让每一个让人记住的角色都有完整的资料页。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "专题策划",
    text: "围绕主题把动画、新闻与角色串成合集，方便用户按兴趣系统浏览内容。",
  },
];

const terms = [
  "本站所有动画、新闻、角色及相关名称均为虚构示例，仅用于展示内容组织与浏览体验，与现实中的任何作品、人物或机构无关。",
  "本站不提供用户登录、在线支付、资源下载及在线播放功能；收藏与分享为纯前端演示，数据仅保存在你的浏览器本地。",
  "站内信息可能随内容更新而调整，如有疑问或发现内容问题，欢迎通过下方邮箱与我们联系。",
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 风车动漫网",
    description:
      "了解风车动漫网的网站定位、内容范围、联系方式与使用须知。风车动漫网致力于为动漫爱好者提供优质的动漫资源与最新资讯浏览体验。",
    keywords: "关于风车动漫网,网站介绍,联系方式,风车动漫网",
  });

  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(email);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Layout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <p className="mt-4 font-label text-xs font-medium uppercase tracking-widest text-accent-600">
            About Us
          </p>
          <h1 className="mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">关于风车动漫网</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            风车动漫网是一个面向青少年及年轻动漫爱好者的动漫资源与资讯分享平台，致力于让获取动漫内容变得简单、愉快。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
        <section className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div className="md:col-span-2">
            <h2 className="mb-3 text-xl font-bold text-foreground-950">网站定位</h2>
            <p className="text-sm leading-7 text-foreground-700">
              我们相信，喜欢动漫的人需要的不只是零散的信息，而是一个能顺畅浏览、方便探索的地方。风车动漫网围绕“资源”与“资讯”双主线搭建内容：既有可一眼种草的动画推荐，也有紧跟动态的新闻资讯，还有值得深入了解的角色资料与主题专题。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              无论你是想寻找下一部要追的动画，还是想了解最新的业界动态，抑或是想回味某个喜欢的角色，都能在这里找到对应的入口。
            </p>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="mb-2 flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-map-pin-line text-primary-600"></i>
                内容范围
              </h3>
              <p className="text-sm leading-relaxed text-foreground-600">
                本站内容围绕动画推荐、动漫新闻、角色介绍与专题策划四大板块组织，所有条目均配有统一的封面、简介与标签，保证列表与详情信息一致，方便检索与浏览。
              </p>
            </div>
          </div>

          <aside className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 text-base font-bold text-foreground-950">联系方式</h2>
            <p className="text-sm leading-relaxed text-foreground-600">
              如有内容建议、合作意向或问题反馈，欢迎通过邮箱联系我们，我们会尽快回复。
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-3">
              <i className="ri-mail-line text-lg text-primary-600"></i>
              <span className="flex-1 break-all text-sm text-foreground-800">{email}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                {copied ? "已复制" : "复制邮箱"}
              </button>
              <a
                href={`mailto:${email}`}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-send-plane-line"></i>
                发送邮件
              </a>
            </div>
            <p className="mt-3 text-xs text-foreground-400">
              以上邮箱为演示地址，不接入真实邮件服务。
            </p>
          </aside>
        </section>

        <section className="mt-12">
          <h2 className="mb-5 text-xl font-bold text-foreground-950">内容板块说明</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {scopeItems.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-foreground-950">
            <i className="ri-shield-check-line text-accent-500"></i>
            版权声明与使用须知
          </h2>
          <ul className="space-y-3">
            {terms.map((term, index) => (
              <li key={index} className="flex gap-3 text-sm leading-7 text-foreground-700">
                <span className="mt-2.5 h-1.5 w-1.5 flex-none rounded-full bg-accent-400"></span>
                <span>{term}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category/recommend"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              开始浏览动画推荐
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              返回首页
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}